import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import api, { fileUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Breadcrumbs from '../components/Breadcrumbs';
import InspectionBanner from '../components/InspectionBanner';
import GroupedProofGallery from '../components/GroupedProofGallery';
import ReportBuilderDialog from '../components/ReportBuilderDialog';
import { StatCard, StatusBar, StatChips, DamageGauge, damageTone } from '../components/metrics';
import LineChart from '../components/LineChart';
import HeatGrid from '../components/HeatGrid';
import { PeriodFilter, ActivityRow, periodLabel } from '../components/PeriodFilter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { POSITIONS, ACTION_LABELS, SITE_STATUS } from '../lib/status';
import { Building2, Boxes, Plus, Loader2, Pencil, Trash2, ArrowRight, Layers, TriangleAlert, Receipt, TrendingUp, Grid3x3, Layers3, X, Megaphone, ClipboardList, Images, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

const ALL_POS = ['poster_left', 'poster_right', 'door_vinyl', 'door_side'];

export default function SocietyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const inspection = params.get('inspection');
  const inspQ = inspection ? `?inspection=${inspection}` : '';
  const canEdit = ['admin', 'manager'].includes(user.role);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [newTower, setNewTower] = useState('');
  const [edit, setEdit] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState('overview');

  const load = useCallback(() => api.get(`/societies/${id}/detail?days=${days}`).then((r) => setData(r.data)).catch(() => setData(false)), [id, days]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/societies/${id}/trends?days=${days || 90}`).then((r) => setTrends(r.data.series)).catch(() => {}); }, [id, days]);

  const addTower = async () => { if (!newTower) return; await api.post(`/societies/${id}/towers`, { name: newTower }); setNewTower(''); load(); toast.success('Tower added'); };
  const saveEdit = async () => { await api.patch(`/towers/${edit.id}`, { name: edit.name }); setEdit(null); load(); toast.success('Renamed'); };
  const delTower = async (t) => { if (!window.confirm(`Delete ${t.name} and all its lifts/sites?`)) return; await api.delete(`/towers/${t.id}`); load(); toast.success('Tower deleted'); };

  if (data === false) return <p className="text-muted-foreground">Society not found or not permitted.</p>;
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const { society, stats, towers, billing, activity } = data;

  return (
    <div className="space-y-5">
      {inspection && <InspectionBanner inspectionId={inspection} />}
      <Breadcrumbs items={[{ label: 'Societies', to: '/societies' }, { label: society.name }]} />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="overline text-primary flex items-center gap-2"><Building2 className="h-3.5 w-3.5" /> Society</p>
          <h1 className="font-head text-3xl font-bold">{society.name}</h1>
          <p className="text-sm text-muted-foreground">{society.address || society.city}</p>
        </div>
        {inspection && <span className="rounded-full border border-sky-600 bg-sky-950/60 px-3 py-1 text-xs text-sky-300">Inspection mode</span>}
        <div className="flex gap-2">
          <ReportBuilderDialog scope={{ society_id: id }} groupByDefault="tower" defaults={{ title: `${society.name} - Inspection Report` }} />
          <Link to="/billing" className="hidden sm:block"><Button variant="secondary"><Receipt className="mr-1 h-4 w-4" /> Billing</Button></Link>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex w-full flex-wrap justify-start">
          <TabsTrigger value="overview" data-testid="sd-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="structure" data-testid="sd-tab-structure">Structure</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="sd-tab-campaigns"><Megaphone className="mr-1 h-3.5 w-3.5" />Campaigns</TabsTrigger>
          <TabsTrigger value="inspections" data-testid="sd-tab-inspections"><ClipboardList className="mr-1 h-3.5 w-3.5" />Inspections</TabsTrigger>
          <TabsTrigger value="proofs" data-testid="sd-tab-proofs"><Images className="mr-1 h-3.5 w-3.5" />Proofs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-5">
          <div className="grid gap-4 lg:grid-cols-4">
            <div className="grid grid-cols-2 gap-3 lg:col-span-3">
              <StatCard label="Sites" value={stats.total} sub={`${stats.installed} deployed`} icon={Layers} />
              <StatCard label="Occupancy" value={`${stats.occupancy_pct}%`} sub={`${stats.occupied}/${stats.total} occupied`} icon={Boxes} tone="text-blue-400" />
              <StatCard label="Damaged" value={stats.damaged} sub="need attention" icon={TriangleAlert} tone={damageTone(stats.damage_pct)} />
              <StatCard label="Towers" value={towers.length} sub={`${billing?.billing_status || 'no billing'}`} icon={Building2} tone="text-primary" />
            </div>
            <div className="rounded-xl border border-border bg-card p-4"><DamageGauge stats={stats} /></div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4 lg:col-span-2">
              <p className="overline mb-2 text-muted-foreground">Status Breakdown</p>
              <StatusBar counts={stats.status_counts} total={stats.total} className="mb-3" />
              <StatChips counts={stats.status_counts} />
            </div>
            <div className="rounded-xl border border-border bg-card p-4" data-testid="society-billing-card">
              <p className="overline mb-2 flex items-center gap-2 text-muted-foreground"><Receipt className="h-3.5 w-3.5" /> Billing</p>
              <p className={`font-head text-2xl font-extrabold ${billing.outstanding > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>₹{Number(billing.outstanding || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-muted-foreground">outstanding balance</p>
              <div className="mt-2 flex items-center gap-2">
                <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase">{billing.billing_status || 'no profile'}</span>
                {billing.outstanding > 0 && (billing.billing_status === 'overdue' || (billing.rent_amount && billing.outstanding >= billing.rent_amount * 2)) && <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-[10px] uppercase text-rose-300">Overdue</span>}
              </div>
              <Link to="/billing" className="mt-3 inline-flex items-center text-xs text-primary">Open ledger <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-4" data-testid="society-trends">
              <p className="mb-2 flex items-center gap-2 font-head text-lg font-bold"><TrendingUp className="h-4 w-4 text-primary" /> Trends (30 days)</p>
              {trends ? <LineChart series={trends} lines={[{ key: 'installed_cum', color: '#2563eb', label: 'Installed' }, { key: 'damage', color: '#ef4444', label: 'Damage events' }]} /> : <div className="flex h-[150px] items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
            </div>
            <div className="rounded-xl border border-border bg-card p-4" data-testid="society-heatmap">
              <p className="mb-2 flex items-center gap-2 font-head text-lg font-bold"><Grid3x3 className="h-4 w-4 text-primary" /> Damage Heatmap</p>
              <HeatGrid towers={towers} />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="overline text-muted-foreground">Activity · {periodLabel(days)}</p>
            <PeriodFilter value={days} onChange={setDays} />
          </div>
          <ActivityRow activity={activity} />
        </TabsContent>

        <TabsContent value="structure" className="mt-4 space-y-5">
          <div className="flex items-center justify-between">
            <p className="font-head text-xl font-bold">Towers</p>
            {canEdit && (
              <div className="flex gap-2">
                <Input placeholder="Tower name" data-testid="sd-new-tower" value={newTower} onChange={(e) => setNewTower(e.target.value)} className="w-40" />
                <Button onClick={addTower} data-testid="sd-add-tower"><Plus className="h-4 w-4" /></Button>
                <Button variant="secondary" data-testid="sd-bulk-btn" onClick={() => setBulkOpen(true)}><Layers3 className="mr-1 h-4 w-4" /> Bulk</Button>
              </div>
            )}
          </div>

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {towers.map((t) => (
              <div key={t.id} data-testid={`society-tower-${t.id}`} onClick={() => navigate(`/towers/${t.id}${inspQ}`)} className="tile-press cursor-pointer rounded-xl border-2 border-border bg-card p-4 hover:border-primary">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/15 text-primary"><Building2 className="h-6 w-6" /></div>
                    <div>
                      <p className="font-head text-lg font-bold">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.lift_count} lifts · {t.stats.total} sites</p>
                    </div>
                  </div>
                  <span className={`font-head font-bold ${damageTone(t.stats.damage_pct)}`}>{t.stats.damage_pct}%</span>
                </div>
                <StatusBar counts={t.stats.status_counts} total={t.stats.total} className="mt-3" />
                <div className="mt-3 flex items-center justify-between">
                  {canEdit ? (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`edit-tower-${t.id}`} onClick={(e) => { e.stopPropagation(); setEdit(t); }}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" data-testid={`del-tower-${t.id}`} onClick={(e) => { e.stopPropagation(); delTower(t); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  ) : <span />}
                  <span className="flex items-center text-xs text-primary">Open <ArrowRight className="ml-1 h-3.5 w-3.5" /></span>
                </div>
              </div>
            ))}
            {!towers.length && <p className="text-sm text-muted-foreground">No towers yet.</p>}
          </div>

          <BulkTowerDialog open={bulkOpen} onClose={() => setBulkOpen(false)} societyId={id} onDone={load} />
          <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
            <DialogContent>
              <DialogHeader><DialogTitle>Rename Tower</DialogTitle><DialogDescription>Update the display name for this tower.</DialogDescription></DialogHeader>
              <Input value={edit?.name || ''} data-testid="edit-tower-input" onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              <DialogFooter><Button onClick={saveEdit} data-testid="save-tower-edit">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="campaigns" className="mt-4">
          <SocietyCampaigns societyId={id} canManage={canEdit} />
        </TabsContent>
        <TabsContent value="inspections" className="mt-4">
          <SocietyInspections societyId={id} />
        </TabsContent>
        <TabsContent value="proofs" className="mt-4">
          <SocietyProofs societyId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SocietyCampaigns({ societyId, canManage }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get('/campaigns').then((r) => setItems(r.data.filter((c) => (c.society_ids || []).includes(societyId)))); }, [societyId]);
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
  return (
    <div className="space-y-3" data-testid="society-campaigns-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} campaign(s) running in this society</p>
        {canManage && <Link to="/campaigns" className="text-xs text-primary hover:underline">Manage campaigns →</Link>}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((c) => (
          <Link key={c.id} to={`/campaigns/${c.id}`} data-testid={`sd-campaign-${c.id}`} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-head text-lg font-bold">{c.name}</p>
                <p className="text-xs text-muted-foreground">{c.client_name} · {c.site_ids?.length || 0} sites</p>
              </div>
              {c.status === 'cancelled'
                ? <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-[10px] uppercase text-rose-300">Cancelled</span>
                : <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] uppercase text-emerald-300">Active</span>}
            </div>
            <p className="mt-2 font-mono text-[11px] text-muted-foreground">{c.start_date || '—'} → {c.end_date || '—'}</p>
          </Link>
        ))}
        {!items.length && <p className="text-sm text-muted-foreground">No campaigns mapped to this society yet.</p>}
      </div>
    </div>
  );
}

function SocietyInspections({ societyId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get('/inspections').then((r) => setItems(r.data.filter((i) => i.society_id === societyId))).catch(() => setItems([])); }, [societyId]);
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
  return (
    <div className="space-y-3" data-testid="society-inspections-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} inspection/install job(s)</p>
        <Link to="/inspections" className="text-xs text-primary hover:underline">Manage inspections →</Link>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((i) => (
          <div key={i.id} data-testid={`sd-inspection-${i.id}`} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-head text-lg font-bold">{i.campaign_name || 'All campaigns'}</p>
                <p className="text-xs text-muted-foreground">Operator: {i.operator_name}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="rounded-full border border-sky-600 bg-sky-950/50 px-2 py-0.5 text-[10px] font-mono uppercase text-sky-300">{i.status}</span>
                <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase text-muted-foreground">{i.mode === 'install' ? 'Install' : 'Inspect'}{i.scope === 'multi' ? ' · Multi' : ''}</span>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> {i.site_ids.length} sites</span>
              {i.due_date && <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> {i.due_date.slice(0, 10)}</span>}
            </div>
            <Button size="sm" className="mt-3 w-full" data-testid={`sd-open-inspection-${i.id}`} onClick={() => navigate(`/societies/${societyId}?inspection=${i.id}`)}>
              Open in Field View <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        ))}
        {!items.length && <p className="text-sm text-muted-foreground">No inspections for this society yet.</p>}
      </div>
    </div>
  );
}

function SocietyProofs({ societyId }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/proofs?society_id=${societyId}&limit=500`).then((r) => setItems(r.data.proofs)).catch(() => setItems([])); }, [societyId]);
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
  return (
    <div data-testid="society-proofs-tab">
      <GroupedProofGallery
        proofs={items}
        allowedGroupings={['tower_lift', 'lift', 'site_type', 'campaign', 'action', 'operator', 'inspection']}
        initialGroupBy="tower_lift"
        emptyMessage="No proof photos captured for this society yet."
      />
    </div>
  );
}

function BulkTowerDialog({ open, onClose, societyId, onDone }) {
  const mkLift = (name, side = null) => ({ name, side, positions: [...ALL_POS] });
  const mkTower = (n) => ({ name: `Tower ${n}`, layout: 'lr', lifts: [mkLift('Left Lift', 'left'), mkLift('Right Lift', 'right')] });
  const [towers, setTowers] = useState([mkTower('New')]);
  const [saving, setSaving] = useState(false);
  const upd = (ti, fn) => setTowers((p) => p.map((t, i) => i === ti ? fn(t) : t));
  const addTower = () => setTowers((p) => [...p, mkTower(p.length + 1)]);
  const rmTower = (ti) => setTowers((p) => p.filter((_, i) => i !== ti));
  const addLift = (ti) => upd(ti, (t) => ({ ...t, lifts: [...t.lifts, mkLift(`Lift ${t.lifts.length + 1}`)] }));
  const rmLift = (ti, li) => upd(ti, (t) => ({ ...t, lifts: t.lifts.filter((_, i) => i !== li) }));
  const setLiftName = (ti, li, v) => upd(ti, (t) => ({ ...t, lifts: t.lifts.map((l, i) => i === li ? { ...l, name: v } : l) }));
  const setLayout = (ti, v) => upd(ti, (t) => ({
    ...t, layout: v,
    lifts: t.lifts.map((l, i) => ({
      ...l,
      name: v === 'lr' ? (i === 0 ? 'Left Lift' : i === 1 ? 'Right Lift' : `Lift ${i + 1}`) : `Lift ${i + 1}`,
      side: v === 'lr' ? (l.side || (i === 1 ? 'right' : 'left')) : null,
    })),
  }));
  const setLiftSide = (ti, li, side) => upd(ti, (t) => ({ ...t, lifts: t.lifts.map((l, i) => i === li ? { ...l, side } : l) }));
  const togglePos = (ti, li, pos) => upd(ti, (t) => ({ ...t, lifts: t.lifts.map((l, i) => i === li ? { ...l, positions: l.positions.includes(pos) ? l.positions.filter((z) => z !== pos) : [...l.positions, pos] } : l) }));
  const totalLifts = towers.reduce((a, t) => a + t.lifts.length, 0);
  const totalSites = towers.reduce((a, t) => a + t.lifts.reduce((b, l) => b + l.positions.length, 0), 0);

  const save = async () => {
    if (towers.some((t) => !t.name.trim())) return toast.error('Every tower needs a name');
    setSaving(true);
    try {
      let lifts = 0, sites = 0;
      for (const t of towers) {
        const r = await api.post(`/societies/${societyId}/bulk-tower`, { tower_name: t.name, lifts: t.lifts, lift_layout: t.layout || 'lr' });
        lifts += r.data.lifts; sites += r.data.sites;
      }
      toast.success(`Created ${towers.length} towers · ${lifts} lifts · ${sites} sites`);
      onClose(); setTowers([mkTower('New')]); onDone();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-2xl">Bulk Create Structure</DialogTitle><DialogDescription>Add multiple towers, each with lifts and their site positions, in one go.</DialogDescription></DialogHeader>
        <div className="flex flex-wrap gap-2 text-xs" data-testid="bulk-totals">
          <span className="rounded-full border border-border px-2.5 py-1">Towers: <b>{towers.length}</b></span>
          <span className="rounded-full border border-border px-2.5 py-1">Lifts: <b>{totalLifts}</b></span>
          <span className="rounded-full border border-border px-2.5 py-1">Sites: <b>{totalSites}</b></span>
        </div>
        <div className="space-y-3">
          {towers.map((t, ti) => (
            <div key={ti} className="rounded-lg border-2 border-border p-3" data-testid={`bulk-tower-${ti}`}>
              <div className="mb-2 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                <Input value={t.name} className="h-8 font-semibold" data-testid={`bulk-tower-name-${ti}`} onChange={(e) => upd(ti, (x) => ({ ...x, name: e.target.value }))} />
                <select data-testid={`bulk-lift-layout-${ti}`} value={t.layout || 'lr'} onChange={(e) => setLayout(ti, e.target.value)}
                  className="h-8 shrink-0 rounded-md border border-input bg-background px-2 text-xs font-medium" title="Lift left/right configuration">
                  <option value="lr">Left + Right lifts</option>
                  <option value="plain">No left/right</option>
                </select>
                {towers.length > 1 && <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-400" data-testid={`bulk-rm-tower-${ti}`} onClick={() => rmTower(ti)}><X className="h-4 w-4" /></Button>}
              </div>
              <div className="space-y-1.5 pl-2">
                {t.lifts.map((l, li) => (
                  <div key={li} className="rounded border border-border p-1.5" data-testid={`bulk-lift-${ti}-${li}`}>
                    <div className="flex items-center gap-2">
                      <Input value={l.name} className="h-7 text-sm" onChange={(e) => setLiftName(ti, li, e.target.value)} />
                      {(t.layout || 'lr') === 'lr' && (
                        <select data-testid={`bulk-lift-side-${ti}-${li}`} value={l.side || (li === 1 ? 'right' : 'left')} onChange={(e) => setLiftSide(ti, li, e.target.value)}
                          className="h-7 shrink-0 rounded-md border border-input bg-background px-1.5 text-[11px] font-medium" title="Which side is this lift on?">
                          <option value="left">⬅ Left</option>
                          <option value="right">Right ➡</option>
                        </select>
                      )}
                      {t.lifts.length > 1 && <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => rmLift(ti, li)}><X className="h-3.5 w-3.5" /></Button>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {ALL_POS.map((p) => (
                        <label key={p} className="flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px]">
                          <Checkbox checked={l.positions.includes(p)} onCheckedChange={() => togglePos(ti, li, p)} /> {POSITIONS[p]}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="text-xs" data-testid={`bulk-add-lift-${ti}`} onClick={() => addLift(ti)}><Plus className="mr-1 h-3.5 w-3.5" /> Add lift</Button>
              </div>
            </div>
          ))}
        </div>
        <Button variant="secondary" className="w-full" data-testid="bulk-add-tower" onClick={addTower}><Plus className="mr-1 h-4 w-4" /> Add another tower</Button>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="bulk-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create {towers.length} towers · {totalLifts} lifts</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
